import React, { ChangeEvent, useMemo, useRef, useState, CSSProperties, useCallback } from "react";
import { Input } from "../input/SimpleInput";
import { cn } from "../../../lib/utils";
import { Search, Inbox } from "lucide-react";

export interface SearchBarOption {
  id: string;
  label: string;
  // Arbitrary payload to be returned on selection
  value?: unknown;
}

export interface SearchBarProps<T extends SearchBarOption = SearchBarOption> {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  options: T[];
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  // Optional custom filter; if not provided, case-insensitive substring match on label
  filterFn?: (option: T, query: string) => boolean;
  // Custom renderer for each option
  renderOption?: (option: T, query: string, isHighlighted: boolean) => React.ReactNode;
  // Called when an option is clicked/selected
  onSelect?: (option: T) => void;
  // Close dropdown after selection
  closeOnSelect?: boolean;
  // Max height of dropdown (tailwind class)
  maxDropdownHeightClass?: string;
  // Disable specific options and prevent selection/close
  isOptionDisabled?: (option: T) => boolean;
  emptyStateText?: string;
}

function defaultFilter<T extends SearchBarOption>(option: T, query: string): boolean {
  return option.label.toLowerCase().includes(query.toLowerCase());
}

// --- Figma-inspired base styles ---
const styles: { [key: string]: CSSProperties } = {
  dropdown: {
    borderRadius: "8px",
    border: "1px solid #E5E5E5",
    background: "#FFFFFF",
    boxShadow:
      "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
    overflowY: "auto",
    borderTopRightRadius: "0px",
    borderTopLeftRadius: "0px",
    borderTop: "0px"
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    fontSize: "14px",
    lineHeight: "20px",
    cursor: "pointer",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 0",
    fontSize: "14px",
    color: "#6B7280", // Tailwind text-muted-foreground equivalent
    textAlign: "center",
  }
};

export function SearchBar<T extends SearchBarOption = SearchBarOption>({
  placeholder = "Search…",
  value,
  onChange,
  options,
  isOpen,
  onOpenChange,
  className,
  inputClassName,
  dropdownClassName,
  filterFn = defaultFilter,
  renderOption,
  onSelect,
  closeOnSelect = true,
  maxDropdownHeightClass = "max-h-48",
  isOptionDisabled,
  emptyStateText = "No Matches Found",
}: SearchBarProps<T>) {
  const [internalOpen, setInternalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isMouseDownOnDropdown = useRef(false);

  const open = isOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    else setInternalOpen(next);
  };

  const filtered = useMemo(() => {
    if (!value.trim()) return options;
    return options.filter((o) => filterFn(o, value));
  }, [value, options, filterFn]);

  const handleSelect = (option: T) => {
    onSelect?.(option);
    if (closeOnSelect) setOpen(false);
  };

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setOpen(e.target.value.trim().length > 0);
  }, [onChange, setOpen]);


  const handleInputFocus = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleInputBlur = useCallback(() => {
    // Only close if mouse is not down on dropdown to allow option selection
    if (!isMouseDownOnDropdown.current) {
      setOpen(false);
    }
  }, [setOpen]);

  const handleDropdownMouseDown = useCallback(() => {
    isMouseDownOnDropdown.current = true;
  }, []);

  const handleDropdownMouseUp = useCallback(() => {
    isMouseDownOnDropdown.current = false;
  }, []);

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      {/* Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          size="sm"
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className={cn("rounded-[8px] pl-10 focus:outline-none placeholder-gray-400 focus:ring-0 focus:border-gray-300", { "rounded-br-none rounded-bl-none": open }, inputClassName)}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={styles.dropdown}
          className={cn(
            "absolute top-full left-0 right-0 z-50",
            maxDropdownHeightClass,
            dropdownClassName
          )}
          onMouseDown={handleDropdownMouseDown}
          onMouseUp={handleDropdownMouseUp}
        >
          {filtered.length > 0 ? (
            filtered.map((option) => {
              const disabled = isOptionDisabled ? isOptionDisabled(option) : false;
              return (
                <div
                  key={option.id}
                  style={styles.optionRow}
                  className={cn(
                    "hover:bg-[#F5F5F5]",
                    disabled && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={() => {
                    if (disabled) return;
                    handleSelect(option);
                  }}
                >
                  {renderOption ? (
                    renderOption(option, value, false)
                  ) : (
                    <span>{option.label}</span>
                  )}
                </div>
              );
            })
          ) : (
            <div style={styles.emptyState}>
              <Inbox className="h-8 w-8 mb-2 text-muted-foreground" />
              {emptyStateText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
